import { createBrowserRouter, Navigate } from 'react-router';

import { AppShell } from '../components/AppShell';
import { AreaHome } from '../components/AreaHome';
import { BrandedSignIn } from '../features/auth/BrandedSignIn';
import { RedeemLinkPage } from '../features/auth/RedeemLinkPage';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { ConfirmEmailPage } from '../features/account/ConfirmEmailPage';
import { ProfilePage } from '../features/account/ProfilePage';
import { AccountsPage } from '../features/accounting/AccountsPage';
import { BankReconciliationPage } from '../features/accounting/BankReconciliationPage';
import { BudgetsPage } from '../features/accounting/BudgetsPage';
import { ClosingPage } from '../features/accounting/ClosingPage';
import { OwnerStatementsPage } from '../features/accounting/OwnerStatementsPage';
import { ReconciliationPage } from '../features/accounting/ReconciliationPage';
import { ReportsPage } from '../features/accounting/ReportsPage';
import { TrialBalancePage } from '../features/accounting/TrialBalancePage';
import { OwnerDistributionsPage } from '../features/owner/OwnerDistributionsPage';
import { OwnerHomePage } from '../features/owner/OwnerHomePage';
import { OwnerPortalStatementsPage } from '../features/owner/OwnerPortalStatementsPage';
import { LeaseDetailPage } from '../features/leases/LeaseDetailPage';
import { LeasesPage } from '../features/leases/LeasesPage';
import { PaymentsPage } from '../features/leases/PaymentsPage';
import { OwnersPage } from '../features/owners/OwnersPage';
import { FilesPage } from '../features/records/FilesPage';
import { PropertiesPage } from '../features/properties/PropertiesPage';
import { PropertyDetailPage } from '../features/properties/PropertyDetailPage';
import { PortalDocumentsPage } from '../features/resident/PortalDocumentsPage';
import { PortalHomePage } from '../features/resident/PortalHomePage';
import { PortalInspectionsPage } from '../features/resident/PortalInspectionsPage';
import { PortalMaintenancePage } from '../features/resident/PortalMaintenancePage';
import { PortalNoticesPage } from '../features/resident/PortalNoticesPage';
import { PortalPaymentsPage } from '../features/resident/PortalPaymentsPage';
import { ResidentsPage } from '../features/residents/ResidentsPage';
import { PlatformHome } from '../features/platform/PlatformHome';
import { DatabaseUsagePage } from '../features/platform/DatabaseUsagePage';
import {
  DiagnosticsLayout,
  EmailTestPage,
  JobRunHistoryPage,
  RecentProblemsPage,
} from '../features/platform/DiagnosticsPage';
import { ScheduledJobsPage } from '../features/platform/ScheduledJobsPage';
import {
  PlatformSecurityPage,
  PlatformSettingsLayout,
} from '../features/platform/PlatformSecurityPage';
import { PlatformSettingsPage } from '../features/platform/PlatformSettingsPage';
import { SupportLogPage } from '../features/platform/SupportLogPage';
import { SystemStatusLayout, SystemStatusPage } from '../features/platform/SystemStatusPage';
import { BusinessSettingsPage } from '../features/settings/BusinessSettingsPage';
import { NotificationSettingsPage } from '../features/settings/NotificationSettingsPage';
import { PaymentSettingsPage } from '../features/settings/PaymentSettingsPage';
import { SecuritySettingsPage } from '../features/settings/SecuritySettingsPage';
import { SettingsLayout } from '../features/settings/SettingsLayout';
import { UsersSettingsPage } from '../features/settings/UsersSettingsPage';
import { CalendarPage } from '../features/compliance/CalendarPage';
import { LegalCasesPage } from '../features/compliance/LegalCasesPage';
import { LicensesPage } from '../features/compliance/LicensesPage';
import { MessagesPage } from '../features/compliance/MessagesPage';
import { NoticesPage } from '../features/compliance/NoticesPage';
import { RenewalsPage } from '../features/compliance/RenewalsPage';
import { ViolationsPage } from '../features/compliance/ViolationsPage';
import { BillsPage } from '../features/maintenance/BillsPage';
import { InspectionDetailPage } from '../features/maintenance/InspectionDetailPage';
import { InspectionsPage } from '../features/maintenance/InspectionsPage';
import { RecurringPage } from '../features/maintenance/RecurringPage';
import { WorkOrdersPage } from '../features/maintenance/WorkOrdersPage';
import { InviteAcceptPage } from '../features/invite/InviteAcceptPage';
import { VendorsPage } from '../features/vendors/VendorsPage';
import { StaffDashboard } from '../features/staff/StaffDashboard';
import { homePathFor, useMe } from '../lib/me';
import { RequirePlatformAdmin } from './RequirePlatformAdmin';
import { RequireSession } from './RequireSession';

const APPLICANT_FEATURES = [
  'Complete your rental application',
  'Upload supporting documents',
  'Check your application status',
  'Sign your lease online',
];

function HomeRedirect() {
  const { data: me } = useMe();
  return me ? <Navigate to={homePathFor(me.role)} replace /> : null;
}

// One SPA, role-based areas: staff, resident portal, owner portal, applicant.
// Area access here is UX only — the API enforces permissions on every route.
export const router = createBrowserRouter([
  { path: '/o/:slug', element: <BrandedSignIn /> },
  { path: '/signin', element: <RedeemLinkPage /> },
  { path: '/invite', element: <InviteAcceptPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  {
    element: <RequireSession />,
    children: [
      { index: true, element: <HomeRedirect /> },
      {
        path: 'staff',
        element: <AppShell area="staff" />,
        children: [
          { index: true, element: <StaffDashboard /> },
          { path: 'properties', element: <PropertiesPage /> },
          { path: 'properties/:propertyId', element: <PropertyDetailPage /> },
          { path: 'owners', element: <OwnersPage /> },
          { path: 'residents', element: <ResidentsPage /> },
          { path: 'maintenance', element: <WorkOrdersPage /> },
          { path: 'compliance', element: <LicensesPage /> },
          { path: 'compliance/notices', element: <NoticesPage /> },
          { path: 'compliance/violations', element: <ViolationsPage /> },
          { path: 'compliance/cases', element: <LegalCasesPage /> },
          { path: 'compliance/calendar', element: <CalendarPage /> },
          { path: 'leases/renewals', element: <RenewalsPage /> },
          { path: 'messages', element: <MessagesPage /> },
          { path: 'maintenance/bills', element: <BillsPage /> },
          { path: 'maintenance/inspections', element: <InspectionsPage /> },
          { path: 'maintenance/inspections/:inspectionId', element: <InspectionDetailPage /> },
          { path: 'maintenance/recurring', element: <RecurringPage /> },
          { path: 'vendors', element: <VendorsPage /> },
          { path: 'leases', element: <LeasesPage /> },
          { path: 'leases/:leaseId', element: <LeaseDetailPage /> },
          { path: 'payments', element: <PaymentsPage /> },
          { path: 'accounting', element: <TrialBalancePage /> },
          { path: 'accounting/accounts', element: <AccountsPage /> },
          { path: 'accounting/closing', element: <ClosingPage /> },
          { path: 'accounting/reports', element: <ReportsPage /> },
          { path: 'accounting/owner-statements', element: <OwnerStatementsPage /> },
          { path: 'accounting/budgets', element: <BudgetsPage /> },
          { path: 'accounting/banking', element: <BankReconciliationPage /> },
          { path: 'accounting/banking/:id', element: <ReconciliationPage /> },
          { path: 'files', element: <FilesPage /> },
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <BusinessSettingsPage /> },
              { path: 'users', element: <UsersSettingsPage /> },
              { path: 'payments', element: <PaymentSettingsPage /> },
              { path: 'notifications', element: <NotificationSettingsPage /> },
              { path: 'security', element: <SecuritySettingsPage /> },
            ],
          },
        ],
      },
      {
        path: 'platform',
        element: <AppShell area="platform" />,
        children: [
          {
            element: <RequirePlatformAdmin />,
            children: [
              { index: true, element: <PlatformHome /> },
              {
                path: 'status',
                element: <SystemStatusLayout />,
                children: [
                  { index: true, element: <SystemStatusPage /> },
                  { path: 'jobs', element: <ScheduledJobsPage /> },
                  { path: 'database', element: <DatabaseUsagePage /> },
                ],
              },
              {
                path: 'diagnostics',
                element: <DiagnosticsLayout />,
                children: [
                  { index: true, element: <RecentProblemsPage /> },
                  { path: 'runs', element: <JobRunHistoryPage /> },
                  { path: 'email', element: <EmailTestPage /> },
                ],
              },
              { path: 'support', element: <SupportLogPage /> },
              {
                path: 'settings',
                element: <PlatformSettingsLayout />,
                children: [
                  { index: true, element: <PlatformSettingsPage /> },
                  { path: 'security', element: <PlatformSecurityPage /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'portal',
        element: <AppShell area="portal" />,
        children: [
          { index: true, element: <PortalHomePage /> },
          { path: 'payments', element: <PortalPaymentsPage /> },
          { path: 'maintenance', element: <PortalMaintenancePage /> },
          { path: 'inspections', element: <PortalInspectionsPage /> },
          { path: 'notices', element: <PortalNoticesPage /> },
          { path: 'documents', element: <PortalDocumentsPage /> },
        ],
      },
      {
        path: 'owner',
        element: <AppShell area="owner" />,
        children: [
          { index: true, element: <OwnerHomePage /> },
          { path: 'statements', element: <OwnerPortalStatementsPage /> },
          { path: 'distributions', element: <OwnerDistributionsPage /> },
        ],
      },
      {
        path: 'apply',
        element: <AppShell area="apply" />,
        children: [
          {
            index: true,
            element: <AreaHome title="Rental Application" features={APPLICANT_FEATURES} />,
          },
        ],
      },
      {
        path: 'account',
        element: <AppShell />,
        children: [
          { index: true, element: <ProfilePage /> },
          { path: 'confirm-email', element: <ConfirmEmailPage /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
